interface LocalWebviewEvent {
  eventType: LocalWebviewEventType;
  targetModule?: TargetModule;
  fallbackHeight?: number;
}

interface TargetModule {
  id: string;
  delta: number;
}

enum LocalWebviewEventType {
  LandingLoaded = 1,
  WebviewPosition = 2
}

declare var window: any;

export class BroadcastChannelService {
  private static readonly LOCAL_WEBVIEW_CHANNEL = 'nymeria-local-webview';
  private static readonly TIMEOUT_DURATION = 1000; // Duration in milliseconds
  private static timeoutHandler: any;

  public static initializeChannel(): BroadcastChannel {
    return new BroadcastChannel(BroadcastChannelService.LOCAL_WEBVIEW_CHANNEL);
  }

  public static getFocusedModule(selector: string): { id: string, delta: number } | undefined {
    const elementsToCheck = document.querySelectorAll(selector);
    const firstVisibleModule = Array.from(elementsToCheck).find(e => {
      const rect = e.getBoundingClientRect();
      const isVisible = (rect.bottom >= 0 && rect.bottom <= window.innerHeight); // replace 0 with some threshold if needed
      return isVisible;
    });

    if (firstVisibleModule) {
      const rect = firstVisibleModule.getBoundingClientRect();
      const delta = rect.top; // distance from top of viewport
      return { id: firstVisibleModule.id, delta: delta };
    }
  }

  public static listenToLocalWebviewEvents(channel: BroadcastChannel | undefined, isCachedVersion: boolean, selector: string): void {
    if (!channel) return;

    if (isCachedVersion) {
      console.log('Listening for LandingLoaded events on cached version.');
      channel.onmessage = (event: MessageEvent<LocalWebviewEvent>) => {
        const data = event.data;
        if (data?.eventType === LocalWebviewEventType.LandingLoaded) {
          console.log('Received LandingLoaded event. Sending WebviewPosition...');
          channel.postMessage({ eventType: LocalWebviewEventType.WebviewPosition, targetModule: BroadcastChannelService.getFocusedModule(selector), fallbackHeight: window.scrollY });
        }
      };
    } else {
      console.log('Listening for WebviewPosition events on live version.');
      channel.onmessage = (event: MessageEvent<LocalWebviewEvent>) => {
        const data = event.data;
        if (data?.eventType === LocalWebviewEventType.WebviewPosition) {
          console.log('Received WebviewPosition event:', data);
          if (data.targetModule) {
            const targetElement = document.getElementById(data.targetModule.id);
            if (targetElement) {
              const rectTop = targetElement.getBoundingClientRect().top;
              const delta = data.targetModule.delta * -1;
              const elementPosition = rectTop + delta + window.scrollY;
              window.scrollTo({ top: elementPosition, behavior: 'instant' });
            } else {
              if (window.newrelic && window.newrelic.noticeError) {
                window.newrelic.noticeError(`[LocalWebView] Element with id ${data.targetModule.id} not found.`);
              }
              console.warn(`Element with id ${data.targetModule.id} not found.`);
              if (data.fallbackHeight !== undefined) {
                window.scrollTo({ top: data.fallbackHeight, behavior: 'instant' });
              }
            }
          }
          clearTimeout(BroadcastChannelService.timeoutHandler);
          BroadcastChannelService.callCloseLoader();
        }
      };
    }
  }

  public static callCloseLoader(): void {
    console.log('Calling closeLoader if available...');
    if (window.DefaultJsInterface && typeof window.DefaultJsInterface.closeLoader === 'function') {
      window.DefaultJsInterface.closeLoader();
    }
  }

  public static sendLandingLoadedMessage(channel?: BroadcastChannel): void {
    channel?.postMessage({ eventType: LocalWebviewEventType.LandingLoaded });
    BroadcastChannelService.timeoutHandler = setTimeout(() => BroadcastChannelService.callCloseLoader(), BroadcastChannelService.TIMEOUT_DURATION);
  }
}